import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useLegalSettings, replacePlaceholders } from '../hooks/useLegalSettings';

export function TermsPage() {
  const { data: settings, isLoading } = useLegalSettings();

  // Replace placeholders in the database content
  const processedContent = useMemo(() => {
    if (!settings?.terms_content) return null;
    return replacePlaceholders(settings.terms_content, settings);
  }, [settings]);

  // Fallback values for static content
  const contactEmail = settings?.email || '[E-Mail wird eingerichtet]';
  const companyName = settings?.company_name || settings?.owner_name || 'ParentPath';

  // Get last updated date
  const lastUpdated = settings?.content_updated_at
    ? new Date(settings.content_updated_at).toLocaleDateString('de-DE', {
        month: 'long',
        year: 'numeric',
      })
    : 'Dezember 2024';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-white" />
      </div>
    );
  }

  // If we have database content, render it as HTML
  if (processedContent) {
    return (
      <div className="space-y-4 pb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Nutzungsbedingungen</h1>
          <p className="text-white/60 mt-1">Stand: {lastUpdated}</p>
        </div>

        {/* Render HTML content from database */}
        <div
          className="legal-content prose prose-invert prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: processedContent }}
        />
      </div>
    );
  }

  // Static fallback content
  return (
    <div className="space-y-4 pb-8">
      <div>
        <h1 className="text-2xl font-bold text-white">Nutzungsbedingungen</h1>
        <p className="text-white/60 mt-1">Stand: {lastUpdated}</p>
      </div>

      <div className="space-y-4">
        {/* 1. Geltungsbereich */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">1. Geltungsbereich</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p>
              Diese Nutzungsbedingungen gelten für die Nutzung der ParentPath-App
              (im Folgenden "App" oder "Dienst"), bereitgestellt von {companyName}.
            </p>
            <p>
              Mit der Registrierung und Nutzung der App akzeptieren Sie diese Nutzungsbedingungen
              sowie unsere Datenschutzerklärung.
            </p>
          </div>
        </div>

        {/* 2. Nutzungsvoraussetzungen */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">2. Nutzungsvoraussetzungen</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p><strong className="text-white">Mindestalter:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Die Nutzung der App ist ab 16 Jahren gestattet.</li>
              <li>Personen unter 18 Jahren benötigen die Zustimmung ihrer Erziehungsberechtigten.</li>
              <li>Mit der Registrierung bestätigen Sie, dass Sie mindestens 16 Jahre alt sind.</li>
            </ul>
            <p className="mt-2"><strong className="text-white">Account:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Sie benötigen einen Account zur Nutzung der App.</li>
              <li>Sie sind für die Sicherheit Ihrer Zugangsdaten verantwortlich.</li>
              <li>Pro Person ist nur ein Account gestattet.</li>
            </ul>
          </div>
        </div>

        {/* 3. Leistungsbeschreibung */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">3. Leistungsbeschreibung</h2>
          <div className="text-white/80 space-y-3 text-sm">
            <p>
              ParentPath ist eine App zur Begleitung während der Schwangerschaft für werdende Eltern.
            </p>

            <div>
              <p className="font-medium text-white">Kostenlose Funktionen:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>SSW-Tracker mit Entwicklungsinformationen</li>
                <li>Checklisten (Kliniktasche, Erstausstattung, Behördengänge)</li>
                <li>Kalender für Termine</li>
                <li>Namen-Finder mit Partner-Matching</li>
                <li>Produktcheck für Lebensmittel</li>
                <li>Begrenzte KI-Assistenten-Nutzung (5 Fragen/Monat)</li>
              </ul>
            </div>

            <div>
              <p className="font-medium text-white">Premium-Funktionen:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Unbegrenzte KI-Assistenten-Nutzung</li>
                <li>Erweiterte Funktionen (Details siehe App)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 4. Premium-Abonnement */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">4. Premium-Abonnement</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p><strong className="text-white">Testphase:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Neue Premium-Nutzer erhalten eine 14-tägige kostenlose Testphase.</li>
              <li>Während der Testphase können Sie jederzeit kündigen.</li>
            </ul>

            <p className="mt-2"><strong className="text-white">Abonnement:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Das Abonnement verlängert sich automatisch um den gewählten Zeitraum.</li>
              <li>Die Zahlung erfolgt über Stripe.</li>
              <li>Preise werden vor Abschluss des Abonnements angezeigt.</li>
            </ul>

            <p className="mt-2"><strong className="text-white">Kündigung:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Das Abonnement kann jederzeit zum Ende des Abrechnungszeitraums gekündigt werden.</li>
              <li>Bei Kündigung bleibt der Premium-Zugang bis zum Ende des bezahlten Zeitraums bestehen.</li>
              <li>Es erfolgt keine anteilige Rückerstattung bei vorzeitiger Kündigung.</li>
              <li>Die Kündigung erfolgt über Profil → Mitgliedschaft verwalten.</li>
            </ul>

            <p className="mt-2"><strong className="text-white">Widerrufsrecht:</strong></p>
            <p>
              Als Verbraucher haben Sie ein 14-tägiges Widerrufsrecht. Bei digitalen Inhalten,
              die sofort bereitgestellt werden, erlischt das Widerrufsrecht mit Beginn der
              Ausführung, wenn Sie dem ausdrücklich zugestimmt haben.
            </p>
          </div>
        </div>

        {/* 5. Nutzerverhalten */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">5. Nutzerverhalten und User-Generated Content</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p>Bei der Nutzung der App ist Folgendes untersagt:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Einreichen von beleidigenden, diskriminierenden oder anstößigen Inhalten</li>
              <li>Einreichen von Marken- oder Prominentennamen ohne Berechtigung</li>
              <li>Nutzung der App für rechtswidrige Zwecke</li>
              <li>Manipulation oder Missbrauch der App-Funktionen</li>
              <li>Automatisierte Zugriffe oder Scraping</li>
            </ul>

            <p className="mt-2">
              <strong className="text-white">Moderation:</strong> Alle von Nutzern eingereichten Inhalte (z.B. Namenvorschläge)
              werden vor Veröffentlichung moderiert. Wir behalten uns vor, Inhalte ohne Angabe von
              Gründen abzulehnen oder zu entfernen.
            </p>

            <p className="mt-2">
              Bei Verstößen behalten wir uns vor, Accounts zu sperren oder zu löschen.
            </p>
          </div>
        </div>

        {/* 6. Haftungsausschluss */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">6. Haftungsausschluss</h2>
          <div className="text-white/80 space-y-3 text-sm">
            <div className="p-3 bg-pp-orange/20 border border-pp-orange/30 rounded-[10px]">
              <p className="font-medium text-pp-orange">Wichtiger Hinweis:</p>
              <p className="text-white/80 mt-1">
                Die in der App bereitgestellten Informationen (SSW-Inhalte, KI-Antworten, Produktcheck)
                dienen ausschließlich der allgemeinen Information und ersetzen <strong className="text-white">keine ärztliche
                Beratung, Diagnose oder Behandlung</strong>.
              </p>
            </div>

            <p>
              <strong className="text-white">Medizinische Informationen:</strong> Bei gesundheitlichen Fragen oder Bedenken
              wenden Sie sich immer an eine qualifizierte medizinische Fachperson. Die App ist kein
              Ersatz für professionelle medizinische Beratung.
            </p>

            <p>
              <strong className="text-white">KI-Assistent:</strong> Die Antworten des KI-Assistenten werden von einer
              künstlichen Intelligenz generiert und können Fehler enthalten. Die Informationen
              sollten nicht als medizinischer Rat verstanden werden.
            </p>

            <p>
              <strong className="text-white">Produktcheck:</strong> Die Bewertung von Lebensmitteln basiert auf
              allgemeinen Empfehlungen. Individuelle Unverträglichkeiten oder Allergien werden
              nicht berücksichtigt. Im Zweifel konsultieren Sie Ihren Arzt.
            </p>

            <p>
              <strong className="text-white">Verfügbarkeit:</strong> Wir bemühen uns um eine hohe Verfügbarkeit der App,
              können diese jedoch nicht garantieren. Wartungsarbeiten oder technische Störungen
              können die Nutzung temporär einschränken.
            </p>
          </div>
        </div>

        {/* 7. Geistiges Eigentum */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">7. Geistiges Eigentum</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p>
              Alle Inhalte der App (Texte, Grafiken, Logos, Code) sind urheberrechtlich geschützt
              und Eigentum von {companyName} oder lizenziert.
            </p>
            <p>
              Eine Vervielfältigung, Verbreitung oder Nutzung der Inhalte außerhalb der
              bestimmungsgemäßen App-Nutzung ist ohne ausdrückliche Genehmigung untersagt.
            </p>
          </div>
        </div>

        {/* 8. Änderungen */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">8. Änderungen der Nutzungsbedingungen</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p>
              Wir behalten uns vor, diese Nutzungsbedingungen jederzeit zu ändern. Bei wesentlichen
              Änderungen werden wir Sie per E-Mail oder In-App-Benachrichtigung informieren.
            </p>
            <p>
              Die weitere Nutzung der App nach Inkrafttreten der Änderungen gilt als Zustimmung
              zu den geänderten Bedingungen.
            </p>
          </div>
        </div>

        {/* 9. Kündigung */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">9. Kündigung und Kontolöschung</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p>
              Sie können Ihr Konto jederzeit löschen unter: Profil → Konto löschen.
            </p>
            <p>
              Bei aktiven Premium-Abonnements muss dieses vor der Kontolöschung gekündigt werden.
            </p>
            <p>
              Wir behalten uns vor, Accounts bei Verstößen gegen diese Nutzungsbedingungen
              zu sperren oder zu löschen.
            </p>
          </div>
        </div>

        {/* 10. Schlussbestimmungen */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">10. Schlussbestimmungen</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p>
              <strong className="text-white">Anwendbares Recht:</strong> Es gilt deutsches Recht unter Ausschluss des
              UN-Kaufrechts.
            </p>
            <p>
              <strong className="text-white">Gerichtsstand:</strong> Für Streitigkeiten mit Unternehmern ist Osnabrück
              ausschließlicher Gerichtsstand. Für Verbraucher gelten die gesetzlichen Bestimmungen.
            </p>
            <p>
              <strong className="text-white">Salvatorische Klausel:</strong> Sollten einzelne Bestimmungen unwirksam sein,
              bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.
            </p>
          </div>
        </div>

        {/* Kontakt */}
        <div className="glass-effect rounded-[15px] p-4">
          <h2 className="text-lg font-semibold text-white mb-3">Kontakt</h2>
          <div className="text-white/80 text-sm">
            <p>Bei Fragen zu diesen Nutzungsbedingungen erreichen Sie uns unter:</p>
            <p className="mt-2">
              E-Mail:{' '}
              <a href={`mailto:${contactEmail}`} className="text-pp-orange hover:underline">
                {contactEmail}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
